import { create } from 'zustand';
import { issueRead, onSessionChange } from './session-read';
import { useAuthStore } from './auth.store';
import { useSubscriptionStore } from './subscription.store';
import { submitFeedback } from '../services/support-chat.service';
import { isGloballyHandled, REFUSAL_PROMPTED, streamRefusal } from '../services/api';
import { getAxiosErrorMessage } from '../utils/error';
import {
  AI_UNAVAILABLE,
  AI_UNAVAILABLE_MESSAGE,
  AiUnavailableError,
  isAiUnavailable,
} from '../utils/ai-unavailable';

/** The reply to a failed send. OpenAI out of credits gets its own words (ai_unavailable). */
const ERROR_REPLY = 'Sorry, I encountered an error. Please try again.';

const API_URL = import.meta.env.VITE_API_URL;

// Cap history sent to API to prevent token bloat
const MAX_HISTORY_MESSAGES = 20;

export type IntakeType =
  | 'bug_report'
  | 'feature_request'
  | 'partnership_inquiry'
  | 'enterprise_licensing'
  | 'testimonial'
  | 'content_contribution'
  | 'press_inquiry'
  | 'integration_request'
  | 'speaker_request';

/**
 * A proposal carried by an assistant message — the AI gathered enough
 * info and is asking the user to confirm before a ticket is actually
 * filed. The user clicks Submit/Edit/Cancel on the rendered ProposalCard;
 * NO row is created in user_feedback until Submit is clicked.
 *
 * `status` flips through the lifecycle as the user interacts:
 *   - 'pending'    — newly proposed; buttons interactive
 *   - 'submitting' — Submit clicked, POST in flight
 *   - 'submitted'  — POST succeeded; AI follows up with a confirm message
 *   - 'cancelled'  — user dismissed; AI returns to conversation
 *   - 'editing'    — user clicked Edit; AI re-opens the topic for more info
 *   - 'error'      — POST failed (network or server-side validator); user can retry
 */
export type ProposalStatus =
  | 'pending'
  | 'submitting'
  | 'submitted'
  | 'cancelled'
  | 'editing'
  | 'error';

export interface SupportProposal {
  type: IntakeType;
  title: string;
  description: string;
  consent_given?: boolean;
  status: ProposalStatus;
  errorMessage?: string;
}

export interface SupportMessage {
  role: 'user' | 'assistant';
  content: string;
  /**
   * Set on the assistant message that proposed the submission. Renders
   * as a ProposalCard with Submit/Edit/Cancel buttons. The user — not
   * the AI — drives the row creation by clicking Submit.
   */
  proposal?: SupportProposal;
}

interface SupportChatState {
  messages: SupportMessage[];
  isOpen: boolean;
  isLoading: boolean;
}

interface SupportChatActions {
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
  /**
   * `REFUSAL_PROMPTED`: the global UpgradePrompt answered a structured 402 /
   * 403, the message was not sent (its bubble is gone) and the panel puts
   * the draft back.
   */
  sendMessage: (message: string) => Promise<'sent' | 'failed' | typeof REFUSAL_PROMPTED>;
  resetConversation: () => void;
  submitProposal: (messageIndex: number) => Promise<void>;
  editProposal: (messageIndex: number) => void;
  cancelProposal: (messageIndex: number) => void;
}

type SupportChatStore = SupportChatState & SupportChatActions;

// Track the active AbortController so we can cancel in-flight streams
let activeAbortController: AbortController | null = null;

export const useSupportChatStore = create<SupportChatStore>((set, get) => ({
  messages: [],
  isOpen: false,
  isLoading: false,

  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),
  setOpen: (open: boolean) => {
    if (!open) {
      // Abort any in-flight stream when closing the panel
      activeAbortController?.abort();
      activeAbortController = null;
    }
    set({ isOpen: open });
  },

  sendMessage: async (message: string) => {
    const token = useAuthStore.getState().token;
    // The session this send goes out under: a refusal answered for another
    // one raises no prompt (#940).
    const sentUnder = issueRead();
    const currentMessages = get().messages;

    // Abort any previous in-flight stream
    activeAbortController?.abort();
    const abortController = new AbortController();
    activeAbortController = abortController;

    // Add user message
    const sent: SupportMessage = { role: 'user', content: message };
    const updatedMessages: SupportMessage[] = [...currentMessages, sent];
    set({ messages: updatedMessages, isLoading: true });

    // Build context from current app state. No credit numbers: the server
    // reads the balance from the user (backlog c-145).
    const subscription = useSubscriptionStore.getState();
    const context = {
      tier: subscription.planId || 'unknown',
      route: window.location.pathname,
      platform: 'web',
      userAgent: navigator.userAgent,
    };

    // Build history (exclude current message), capped to prevent token bloat
    const allHistory = updatedMessages.slice(0, -1).map((m) => ({
      role: m.role,
      content: m.content,
    }));
    const history = allHistory.slice(-MAX_HISTORY_MESSAGES);

    try {
      const response = await fetch(`${API_URL}/support/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message,
          context,
          history,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        // fetch skips the axios interceptor: the refusal goes to the same
        // global handling here (#1058 follow-up).
        const refused = await streamRefusal(response, sentUnder);
        if (refused) throw refused;
        const errorData = await response.json().catch(() => ({}));
        if (isAiUnavailable(errorData)) throw new AiUnavailableError();
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
      }

      // Add empty assistant message to stream into
      set({
        messages: [...get().messages, { role: 'assistant', content: '' }],
      });

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let assistantContent = '';
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Buffer partial lines across chunks to handle network packet splits
        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        // Keep the last (potentially incomplete) line in the buffer
        sseBuffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const data = trimmed.slice(6);
          if (data === '[DONE]') continue;

          let json: Record<string, unknown>;
          try {
            json = JSON.parse(data);
          } catch {
            // Ignore JSON parse errors for incomplete SSE chunks
            continue;
          }

          if (json.content) {
            assistantContent += json.content as string;
            const msgs = get().messages;
            set({
              messages: [...msgs.slice(0, -1), { role: 'assistant', content: assistantContent }],
            });
          }

          if (json.done) {
            // Action payload becomes a PROPOSAL on the assistant message.
            // The user reviews the captured info in a ProposalCard and
            // clicks Submit/Edit/Cancel. NO row is filed until Submit.
            // This is the layer-2 gate (after prompt rules + before
            // server validator) — user has explicit agency over what
            // becomes a support ticket. See
            // docs/plans/SUPPORT-CHAT-STUB-MARKER-FIX.md for context.
            const action = json.action as Record<string, unknown> | undefined;
            if (action?.type && action?.title) {
              const msgs = get().messages;
              const lastIdx = msgs.length - 1;
              const last = msgs[lastIdx];
              if (last?.role === 'assistant') {
                const updated: SupportMessage = {
                  ...last,
                  proposal: {
                    type: action.type as IntakeType,
                    title: action.title as string,
                    description: (action.description as string) || assistantContent,
                    consent_given: action.consent_given === true ? true : undefined,
                    status: 'pending',
                  },
                };
                set({
                  messages: [...msgs.slice(0, -1), updated],
                });
              }
            }
            set({ isLoading: false });
          }

          if (json.error) {
            throw json.error === AI_UNAVAILABLE
              ? new AiUnavailableError()
              : new Error(json.error as string);
          }
        }
      }

      set({ isLoading: false });
      return 'sent';
    } catch (error) {
      // Don't show error for intentional aborts
      if (error instanceof DOMException && error.name === 'AbortError') {
        set({ isLoading: false });
        return 'failed';
      }
      // The prompt is the one answer: no error bubble, and the refused
      // message's own bubble goes (the server kept nothing).
      if (isGloballyHandled(error)) {
        set({ messages: get().messages.filter((m) => m !== sent), isLoading: false });
        return REFUSAL_PROMPTED;
      }

      console.error('Support chat error:', error);
      // Add error message
      const reply = isAiUnavailable(error) ? AI_UNAVAILABLE_MESSAGE : ERROR_REPLY;
      const msgs = get().messages;
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg?.role === 'assistant' && lastMsg.content === '') {
        // Replace empty assistant message with error
        set({
          messages: [...msgs.slice(0, -1), { role: 'assistant', content: reply }],
          isLoading: false,
        });
      } else if (lastMsg === sent) {
        // Failed before any answer began (a 500): say so under the message (b-105).
        set({
          messages: [...msgs, { role: 'assistant', content: reply }],
          isLoading: false,
        });
      } else {
        set({ isLoading: false });
      }
      return 'failed';
    } finally {
      if (activeAbortController === abortController) {
        activeAbortController = null;
      }
    }
  },

  resetConversation: () => {
    // Abort any in-flight stream when resetting
    activeAbortController?.abort();
    activeAbortController = null;
    set({ messages: [], isLoading: false });
  },

  /**
   * User clicked Submit on a proposal card. POST to the feedback endpoint
   * (which runs the server-side validator); on success, append a
   * confirmation message; on failure, surface the error inline.
   */
  submitProposal: async (messageIndex: number) => {
    const msgs = get().messages;
    const msg = msgs[messageIndex];
    if (!msg?.proposal || msg.proposal.status !== 'pending') return;

    const setProposalStatus = (status: ProposalStatus, errorMessage?: string) => {
      const current = get().messages;
      const target = current[messageIndex];
      if (!target?.proposal) return;
      const updated: SupportMessage = {
        ...target,
        proposal: { ...target.proposal, status, errorMessage },
      };
      set({
        messages: [...current.slice(0, messageIndex), updated, ...current.slice(messageIndex + 1)],
      });
    };

    setProposalStatus('submitting');

    const { type, title, description, consent_given } = msg.proposal;
    try {
      await submitFeedback({
        type,
        title,
        description,
        metadata: {
          source: 'support_chat',
          platform: 'web',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          ...(consent_given != null && { consent_given }),
        },
      });

      setProposalStatus('submitted');

      // Type-specific friendly confirmation, appended as a NEW assistant
      // message so the proposal card stays visible (shows what was filed).
      //
      // Expectation-setting policy (deliberate): only the types we actually
      // reply to individually promise follow-up. Bug reports and feature
      // requests are reviewed but NOT individually answered, so their copy
      // affirms the value of the report and is honest that a personal reply
      // isn't guaranteed — rather than implying an inbox response that never
      // comes. The human-follow-up types (partnership, enterprise, press,
      // integration, speaker) keep an explicit "we'll be in touch" because a
      // person does email those back.
      const confirmMessages: Record<IntakeType, string> = {
        bug_report:
          'Thanks, this is logged. We review every report and fix what we can. You may not hear back individually, but it genuinely helps us improve EmpathEase.',
        feature_request:
          "Logged, thank you. We read every request when we plan what's next. We can't promise a timeline, but it's on our radar now.",
        partnership_inquiry: 'Got it. A human will personally email you within a few days.',
        enterprise_licensing: 'Got it. Our team will personally reach out within a few days.',
        testimonial: 'Thank you for this. Your consent is recorded and our team will review it.',
        content_contribution:
          'Thank you for sharing your story. Your consent is recorded and we may reach out about publishing it.',
        press_inquiry: 'Logged. Our communications team will follow up with you directly.',
        integration_request: "Logged. Our team will review it and follow up if it's a fit.",
        speaker_request: "Got it. We'll be in touch about availability.",
      };
      set({
        messages: [...get().messages, { role: 'assistant', content: confirmMessages[type] }],
      });
    } catch (err) {
      // Prefer the server validator's human-readable reason from
      // response.data.message — axios's err.message is just "Request
      // failed with status code 422" which defeats the purpose of the
      // structured rejection. Falls back to generic text if the server
      // didn't provide one.
      let errorMessage = 'Failed to submit. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { message?: string; error?: string } } })
          .response;
        const dataMessage = response?.data?.message;
        if (typeof dataMessage === 'string' && dataMessage.length > 0) {
          errorMessage = dataMessage;
        }
      } else {
        errorMessage = getAxiosErrorMessage(err, errorMessage);
      }
      setProposalStatus('error', errorMessage);
    }
  },

  /**
   * User clicked Edit. Mark the proposal as 'editing' (UI dims the buttons,
   * shows an "ask me what to change" hint) and send a follow-up message
   * to the AI asking what they'd like to revise.
   */
  editProposal: (messageIndex: number) => {
    const msgs = get().messages;
    const msg = msgs[messageIndex];
    if (!msg?.proposal || msg.proposal.status !== 'pending') return;

    const updated: SupportMessage = {
      ...msg,
      proposal: { ...msg.proposal, status: 'editing' },
    };
    set({
      messages: [...msgs.slice(0, messageIndex), updated, ...msgs.slice(messageIndex + 1)],
    });

    // Kick the AI to ask what to change. The previous assistant turn
    // is still in conversation history (marker text intact, so the AI
    // sees the structured proposal), but naming the type explicitly
    // helps weaker models that might otherwise re-propose from scratch
    // instead of editing. We deliberately DON'T tack on "What should I
    // change?" — the user clicked Edit because they already know what to
    // change, so making them ask the AI that reads backwards. This line
    // reads as a sensible user utterance and still prompts the AI to ask.
    const typeLabel = msg.proposal.type.replace(/_/g, ' ');
    void get().sendMessage(`I'd like to edit my ${typeLabel} submission.`);
  },

  /**
   * User clicked Cancel. Mark as 'cancelled' (UI greys the card) and
   * post a short assistant ack. NO row is created.
   */
  cancelProposal: (messageIndex: number) => {
    const msgs = get().messages;
    const msg = msgs[messageIndex];
    if (!msg?.proposal || msg.proposal.status !== 'pending') return;

    const updated: SupportMessage = {
      ...msg,
      proposal: { ...msg.proposal, status: 'cancelled' },
    };
    set({
      messages: [
        ...msgs.slice(0, messageIndex),
        updated,
        ...msgs.slice(messageIndex + 1),
        {
          role: 'assistant',
          content:
            "No problem — I didn't file anything. Let me know if there's something else I can help with.",
        },
      ],
    });
  },
}));

// The conversation (and any ticket proposal in it) is the previous account's:
// a session change aborts the stream, clears it and closes the panel, so the
// next account cannot submit the previous one's proposal as its own (#940).
onSessionChange(() => {
  useSupportChatStore.getState().resetConversation();
  useSupportChatStore.getState().setOpen(false);
});
